/**
 * 角色控制器，轻量无插件、无物理引擎, 只在水平面运动
 * 模型动作：idle、walking、running
 * 动作控制：wsad(移动)+shift(跑),+\-(移动速度)
 * zhu18 at 2025/03/27
 */
import type { AnimationAction, OrthographicCamera, Scene } from 'three'
import { AnimationClip, AnimationMixer, Camera, Euler, EventDispatcher, Group, LoopOnce, Object3D, PerspectiveCamera, Vector2, Vector3 } from 'three'
import LoaderModule from '../load'
enum EPersonActionType {
  Idle = 'idle', // 空闲
  Walk = 'walking', // 行走
  Run = 'running', // 跑步
  Jump = 'jump', // 跳跃
}

interface IKeys {
  forward: boolean
  backward: boolean
  left: boolean
  right: boolean
  up: boolean
  down: boolean
  run: boolean
  jump: boolean
  increaseSpeed: boolean // 加速
  decreaseSpeed: boolean // 减速
}
export interface ISpawnPoint {
  position?: Vector3
  rotation?: Euler
}

// 时间类型
const EventType = {
  change: { type: 'change' },
}

interface LimitOption {
  pitch?: [number, number]
  yaw?: [number, number]
  distance?: [number, number]
}

/**
 * 角色控制器
 *
 * @export
 * @class CharacterControls
 * @description three通用场景角色控制器
 * @example
 *
 * let controller = new CharacterControls({
 *   scene,
 *   camera,
 *  domElement: document.body,
 *   characterModelUrl: 'path/to/your/3d/model.glb',
 *   spawnPoint: {// 可选， 出生点， 3D模型文件在场景中的初始位置
 *     position: new Vector3(0, 0, 0),// 位置
 *     rotation: new Euler(0, 0, 0)// 视角
 *   }
 * });
 *
 */
export class CharacterControls extends EventDispatcher {
  // 场景设置
  public scene: Scene
  public camera: PerspectiveCamera | OrthographicCamera
  public domElement: HTMLElement

  // 新增出生点属性
  public spawnPoint: ISpawnPoint = {
    position: new Vector3(0, 0, 0),
    rotation: new Euler(0, 0, 0),
  }

  // 角色相关
  public characterModelUrl = ''
  public character: Object3D | null = null
  private mixer: AnimationMixer | null = null
  private animations: Record<string, AnimationAction> = {}
  private currentAction: AnimationAction | null = null
  private currentActionAnimate: EPersonActionType = EPersonActionType.Idle
  private isJumping = false
  private lastJumpTime = 0
  public castShadow = true

  // 添加视角模式属性
  private isFirstPerson = false

  // 控制相关
  public moveSpeed = 0.03
  private keys: IKeys = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    up: false,
    down: false,
    run: false,
    jump: false,
    increaseSpeed: false,
    decreaseSpeed: false,
  }

  // 鼠标控制相关
  private mousePosition: Vector2 = new Vector2()
  private targetRotation: Euler = new Euler()
  private isMouseDown = false
  private mouseSensitivity = 0.002
  private smoothFactor = 0.1
  private rotationSpeed = 0.1
  private minPolarAngle = 0
  private maxPolarAngle: number = Math.PI * 0.45
  private fadeDuration = 0.1
  public minCameraDistance = 5 // Minimum allowed camera distance
  public maxCameraDistance = 20 // Maximum allowed camera distance

  // 相机参数
  public cameraDistance = 5
  public cameraHeight = 1.7
  private currentRotation: Euler = new Euler()
  private jumpHeight = 3
  private jumpSpeed = 10
  private jumpTime = 0
  private initialY = 0

  private _enabled = true
  public allowFly = false // 允许飞行
  public maxMoveSpeed = 0.2// 最大移动速度  走路：0.2 | 飞行：5
  public minMoveSpeed = 0.01// 最小移动速度

  /**
     * 创建角色控制器
     * @param scene threejs scene object
     * @param camera threejs scene camera
     * @param CharacterModelUrl Character model file url
     * @param spawnPoint 角色出生位置
     */
  constructor(options: { scene: Scene; camera: PerspectiveCamera | OrthographicCamera; domElement: HTMLElement; characterModelUrl: string; spawnPoint?: ISpawnPoint; limit: LimitOption }) {
    super()
    if (!options.characterModelUrl)
      console.warn('创建角色控制器时,CharacterModelUrl无效值')

    this.characterModelUrl = options.characterModelUrl

    this._enabled = true
    // 场景设置
    this.scene = options.scene
    this.camera = options.camera
    this.domElement = options.domElement

    // 角色相关
    this.character = new Object3D()
    this.mixer = null
    this.animations = {}
    this.currentAction = null
    this.currentActionAnimate = EPersonActionType.Idle // 当前活动动画
    this.isJumping = false // 跳跃状态标识
    this.lastJumpTime = 0 // 跳跃冷却计时

    // 控制相关
    this.moveSpeed = 0.03 // 移动速度
    this.keys = {
      forward: false,
      backward: false,
      left: false,
      right: false,
      up: false,
      down: false,
      run: false,
      jump: false,
      increaseSpeed: false, // 加速
      decreaseSpeed: false, // 减速
    }
    // 添加鼠标控制相关属性
    this.mousePosition = new Vector2()
    this.targetRotation = new Euler()
    this.isMouseDown = false
    this.mouseSensitivity = 0.002// 鼠标灵敏度
    this.smoothFactor = 0.1// 相机平滑程度
    this.rotationSpeed = 0.1// 旋转速度
    this.minPolarAngle = (options.limit && options.limit.pitch) ? options.limit.pitch[0] : 0 // 仰角最小值（弧度）
    this.maxPolarAngle = (options.limit && options.limit.pitch) ? options.limit.pitch[1] : Math.PI * 0.45 // 仰角最大值（弧度）
    this.fadeDuration = 0.3 // 动画过度时间

    // 相机参数
    this.cameraDistance = 5 // 相机距离
    this.cameraHeight = 1.7 // 相机高度
    this.currentRotation = new Euler()

    this.allowFly = false // 是否可以浮空

    // 初始化出生点
    if (options.spawnPoint) {
      this.spawnPoint = {
        position: options.spawnPoint.position || new Vector3(0, 0, 0),
        rotation: options.spawnPoint.rotation || new Euler(0, 0, 0),
      }
    }

    this.init()
  }

  public get enabled(): boolean {
    return this._enabled
  }

  public set enabled(value: boolean) {
    this._enabled = value
    this.hideCharacterModel(!this._enabled)
  }

  public get target(): Vector3 | undefined {
    return this.character?.position
  }

  public set limit(options: LimitOption) {
    this.minPolarAngle = options.pitch ? options.pitch[0] : 0
    this.maxPolarAngle = options.pitch ? options.pitch[1] : Math.PI * 0.45
  }

  /**
     * 切换第一/第三人称视角
     */
  public toggleView(): void {
    this.isFirstPerson = !this.isFirstPerson
    this.setMode(this.isFirstPerson)
  }

  /**
     * 设置角色控制器的视角模式
     * @param isFirstPerson - 是否为第一人称模式。true表示第一人称，false表示第三人称
     * @remarks
     * - 第一人称模式下：相机距离设为0.1，隐藏角色模型，相机位置调整到角色眼睛高度
     * - 第三人称模式下：相机距离设为5，显示角色模型，相机位置调整到角色后方
     */
  public setMode(isFirstPerson: boolean) {
    if (!this.character)
      return
    // 第一人称
    if (isFirstPerson) {
      this.cameraDistance = 0.1
      this.maxPolarAngle = Math.PI * 0.65 // 仰角最大值（弧度）

      this.camera.position.copy(this.character.position)
      this.camera.position.y += this.cameraHeight
      this.camera.lookAt(this.character.position.add(new Vector3(0, this.cameraHeight, 0)))

      this.hideCharacterModel(true)
    }
    else {
      this.cameraDistance = 5
      this.maxPolarAngle = Math.PI * 0.45 // 仰角最大值（弧度）
      this.camera.position.copy(this.character.position)
      this.camera.position.y -= this.cameraHeight
      this.camera.lookAt(this.character.position.add(new Vector3(0, -this.cameraHeight, 0)))
      // 切换回第三人称时，恢复相机位置
      this.hideCharacterModel(false)
    }
    this.isFirstPerson = isFirstPerson
  }

  // 添加角色模型显隐属性
  private tempCameraPos: Vector3 = new Vector3()
  private hideCharacterModel(hide: boolean): void {
    if (this.character)
      this.character.visible = !hide
  }

  init() {
    // 加载角色模型
    const loader = new LoaderModule()
    if (this.characterModelUrl) {
      loader.loadGLTF({
        url: this.characterModelUrl,
        draco: false,
        onLoad: (gltf) => {
          this.character?.add(gltf.scene)
          if (this.character) {
          // 启用阴影投射
            if (this.castShadow) {
              this.character.traverse((object: any) => {
                if (object.isMesh)
                  object.castShadow = true
              })
            }

            // 设置初始位置和朝向
            this.character.position.copy(this.spawnPoint.position!)
            this.character.rotation.copy(this.spawnPoint.rotation!)

            // 同步相机初始旋转
            this.targetRotation.copy(this.spawnPoint.rotation!)
            this.currentRotation.copy(this.spawnPoint.rotation!)

            this.scene.add(this.character)
            // 设置动画混合器
            this.mixer = new AnimationMixer(this.character)
          }

          gltf.animations?.forEach((clip: any) => {
            if (this.mixer)
              this.animations[clip.name] = this.mixer.clipAction(clip)
          })

          // 播放默认动画
          this.playAnimation(EPersonActionType.Idle)
        },
      })
    }

    // 事件监听
    this.domElement.addEventListener('keydown', e => this.handleKeyDown(e))
    this.domElement.addEventListener('keyup', e => this.handleKeyUp(e))
    this.domElement.addEventListener('resize', () => this.handleResize())

    // 添加鼠标事件监听
    this.domElement.addEventListener('contextmenu', e => this.onContextMenu(e))
    this.domElement.addEventListener('mousedown', e => this.onMouseDown(e))
    this.domElement.addEventListener('mouseup', e => this.onMouseUp(e))
    this.domElement.addEventListener('mousemove', e => this.onMouseMove(e))

    // 可选：添加触摸事件支持
    this.domElement.addEventListener('touchstart', e => this.onTouchStart(e))
    this.domElement.addEventListener('touchend', e => this.onTouchEnd(e))
    this.domElement.addEventListener('touchmove', e => this.onTouchMove(e), { passive: false })

    // 滚轮缩放
    this.domElement.addEventListener('wheel', e => this.onWheel(e), { passive: false })
  }

  private onContextMenu(event: MouseEvent): void {
    if (this.enabled === false)
      return
    event.preventDefault()
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.enabled === false)
      return

    switch (event.key.toLowerCase()) {
      case 'w': this.keys.forward = true; break
      case 's': this.keys.backward = true; break
      case 'a': this.keys.left = true; break
      case 'd': this.keys.right = true; break
      case 'e': this.keys.up = true; break
      case 'q': this.keys.down = true; break
      case 'shift': this.keys.run = true; break
        // case ' ':
        //     if (!this.isJumping) {
        //         this.keys.jump = true;
        //     }
        //     break;
      case '+': // 处理+键加速
        this.keys.increaseSpeed = true
        break
      case '-': // 处理-键减速
        this.keys.decreaseSpeed = true
        break
    }
  }

  private handleKeyUp(event: KeyboardEvent): void {
    switch (event.key.toLowerCase()) {
      case 'w': this.keys.forward = false; break
      case 's': this.keys.backward = false; break
      case 'a': this.keys.left = false; break
      case 'd': this.keys.right = false; break
      case 'e': this.keys.up = false; break
      case 'q': this.keys.down = false; break
      case 'shift': this.keys.run = false; break
        // case ' ': this.keys.jump = false; break;
      case '+':
        this.keys.increaseSpeed = false
        break
      case '-':
        this.keys.decreaseSpeed = false
        break
      case 'v': // 添加V键切换视角
        this.toggleView()
        break
    }
  }

  private onMouseDown(event: MouseEvent): void {
    this.isMouseDown = true
    this.mousePosition.x = event.clientX
    this.mousePosition.y = event.clientY
  }

  private onMouseUp(event: MouseEvent): void {
    this.isMouseDown = false
  }

  private onMouseMove(event: MouseEvent): void {
    if (this.enabled === false)
      return
    if (!this.isMouseDown)
      return

    const deltaX = event.clientX - this.mousePosition.x
    const deltaY = event.clientY - this.mousePosition.y

    this.mousePosition.x = event.clientX
    this.mousePosition.y = event.clientY

    this.rotateCamera(deltaX, deltaY)
  }

  private onTouchStart(event: TouchEvent): void {
    if (event.touches.length === 1) {
      this.isMouseDown = true
      this.mousePosition.x = event.touches[0].clientX
      this.mousePosition.y = event.touches[0].clientY
    }
  }

  private onTouchEnd(event: TouchEvent): void {
    this.isMouseDown = false
  }

  private handleResize(): void {
    if (this.camera instanceof PerspectiveCamera) {
      this.camera.aspect = window.innerWidth / window.innerHeight
      this.camera.updateProjectionMatrix()
    }
  }

  private onTouchMove(event: TouchEvent): void {
    if (!this.isMouseDown || event.touches.length !== 1)
      return

    const deltaX = event.touches[0].clientX - this.mousePosition.x
    const deltaY = event.touches[0].clientY - this.mousePosition.y

    this.mousePosition.x = event.touches[0].clientX
    this.mousePosition.y = event.touches[0].clientY

    this.rotateCamera(deltaX, deltaY)
  }

  private rotateCamera(deltaX: number, deltaY: number): void {
    // 水平旋转
    this.targetRotation.y -= deltaX * this.mouseSensitivity

    // 垂直旋转（带限制）
    this.targetRotation.x -= deltaY * this.mouseSensitivity
    this.targetRotation.x = Math.max(this.minPolarAngle, Math.min(this.maxPolarAngle, this.targetRotation.x))
    this.dispatchEvent(EventType.change)
  }

  private playAnimation(name: string): void {
    if (this.currentAction) {
      if (this.currentAction.getClip().name === name)
        return
      const oldAction = this.currentAction
      const newAction = this.animations[name]
      newAction.reset()
      newAction.play()
      newAction.crossFadeFrom(oldAction, this.fadeDuration, true)

      this.currentAction = newAction
    }
    else if (this.animations[name]) {
      this.currentAction = this.animations[name]
      this.currentAction.play()
    }
  }

  // Add a new method to handle wheel events
  private onWheel(event: WheelEvent): void {
    if (this.enabled === false)
      return
    event.preventDefault()

    // Adjust camera distance based on scroll direction
    const zoomAmount = event.deltaY * 0.01
    this.cameraDistance = Math.max(
      this.minCameraDistance,
      Math.min(this.maxCameraDistance, this.cameraDistance + zoomAmount),
    )
    this.dispatchEvent(EventType.change)
  }

  private updateCamera(): void {
    if (!this.character)
      return

    this.currentRotation.x += (this.targetRotation.x - this.currentRotation.x) * this.smoothFactor
    this.currentRotation.y += (this.targetRotation.y - this.currentRotation.y) * this.smoothFactor

    const theta = this.currentRotation.y
    const phi = this.currentRotation.x

    const x = this.cameraDistance * Math.sin(phi) * Math.sin(theta)
    const y = this.cameraDistance * Math.cos(phi)
    const z = this.cameraDistance * Math.sin(phi) * Math.cos(theta)

    this.camera.position.set(
      this.character.position.x + x,
      this.character.position.y + y,
      this.character.position.z + z,
    )

    this.camera.lookAt(this.character.position)

    // 根据相机方向更新角色移动
    this.updateCharacter()
  }

  /**
     * 更新角色移动和动画状态
     *
     * @param delta 可选的时间增量参数，用于帧率无关的更新
     * @description
     * - 根据键盘输入计算移动向量
     * - 处理角色移动逻辑（前进/后退/左移/右移）
     * - 更新角色朝向使其与移动方向一致
     * - 根据移动状态切换行走/跑步动画
     * - 无输入时播放待机动画
     * - 包含被注释掉的跳跃功能实现
     * @private 内部实现细节，不对外暴露
     */
  private updateCharacter(delta?: number): void {
    if (!this.character)
      return

    let moving = false
    const speed = this.keys.run ? this.moveSpeed * 2 : this.moveSpeed

    // 获取相机的方向
    const cameraDirection = new Vector3()
    this.camera.getWorldDirection(cameraDirection)
    if (!this.allowFly)
      cameraDirection.y = 0 // 保持水平移动

    cameraDirection.normalize()

    // 计算右向量
    const rightVector = new Vector3()
    rightVector.crossVectors(cameraDirection, new Vector3(0, 1, 0))

    // 移动向量
    const moveVector = new Vector3()

    if (this.keys.forward) {
      moveVector.add(cameraDirection)
      moving = true
    }
    if (this.keys.backward) {
      moveVector.sub(cameraDirection)
      moving = true
    }
    if (this.keys.left) {
      moveVector.sub(rightVector)
      moving = true
    }
    if (this.keys.right) {
      moveVector.add(rightVector)
      moving = true
    }

    // 处理Q和E按键 - 第一人称模式下上升/下降
    if (this.isFirstPerson && this.allowFly) {
      if (this.keys.down)
        this.character.position.y -= speed

      if (this.keys.up)
        this.character.position.y += speed
    }

    // 处理跳跃（带冷却检测）
    // if (this.keys.jump && !this.isJumping && Date.now() - this.lastJumpTime > 500) {
    //     this.lastJumpTime = Date.now();
    //     this.isJumping = true;
    //     this.jumpTime = 0; // 重置跳跃计时器
    //     this.initialY = this.character.position.y; // 记录起跳高度

    //     const jumpAction = this.animations[EPersonActionType.Jump];
    //     jumpAction.reset();
    //     jumpAction.play();
    //     jumpAction.clampWhenFinished = true;

    //     // 初始化跳跃参数（可在构造函数中配置）
    //     this.jumpHeight = 3; // 暴露可调节的跳跃高度变量
    //     this.jumpSpeed = 10;  // 跳跃速度系数（值越大跳跃越快）

    //     // 跳跃完成后回到待机状态
    //     jumpAction.setLoop(LoopOnce, 1);
    //     this.mixer && this.mixer.addEventListener('finished', (e) => {
    //         if (e.action === jumpAction) {
    //             this.isJumping = false;
    //             this.playAnimation(this.currentActionAnimate);
    //         }
    //     });
    // }

    // 如果有移动输入
    if (moving) {
      // 标准化移动向量
      moveVector.normalize()
      // 应用移动
      this.character.position.add(moveVector.multiplyScalar(speed))
      // 更新角色朝向
      this.character.rotation.y = Math.atan2(moveVector.x, moveVector.z)
      // this.character.rotation.y = Math.atan2(moveVector.x, moveVector.z) + Math.PI;

      if (this.keys.run)
        this.currentActionAnimate = EPersonActionType.Run // 跑步
      else
        this.currentActionAnimate = EPersonActionType.Walk //  行走

      this.dispatchEvent(EventType.change)
      // 更新动画
      this.playAnimation(this.currentActionAnimate)
    }
    else {
      this.playAnimation(EPersonActionType.Idle)// 待机动画
    }
  }

  public update(): void {
    if (!this.enabled)
      return
    const delta = 0.016 // 假设60fps
    if (this.mixer)
      this.mixer.update(delta)

    // 处理速度调整
    if (this.keys.increaseSpeed)
      this.moveSpeed = Math.min(this.moveSpeed + this.maxMoveSpeed / 50, this.maxMoveSpeed) // 最大速度0.2         + 0.005

    if (this.keys.decreaseSpeed)
      this.moveSpeed = Math.max(this.moveSpeed - this.maxMoveSpeed / 50, this.minMoveSpeed) // 最小速度0.01       - 0.005

    // 更新跳跃状态
    if (this.isJumping) {
      this.jumpTime += delta * this.jumpSpeed
      // 正弦曲线运动（完整周期后自动结束跳跃）
      if (this.character) {
        this.character.position.y = this.initialY + Math.sin(this.jumpTime) * this.jumpHeight

        // 自动结束跳跃（当完成一个完整的正弦周期时）
        if (this.jumpTime >= Math.PI) {
          this.isJumping = false
          this.character.position.y = this.initialY // 确保回到初始高度
          this.playAnimation(this.currentActionAnimate)
        }
      }
    }

    this.updateCamera()
  }

  // 销毁角色控制器
  public dispose(): void {
    // 清空动画动作缓存
    if (this.mixer) {
      this.mixer.stopAllAction()
      this.mixer = null
    }
    this.animations = {}

    // 从场景中移除角色
    if (this.character && this.scene) {
      this.scene.remove(this.character)
      this.character = null
    }

    // 移除事件
    if (!this.domElement)
      return
    this.domElement.removeEventListener('keydown', e => this.handleKeyDown(e))
    this.domElement.removeEventListener('keyup', e => this.handleKeyUp(e))
    this.domElement.removeEventListener('resize', () => this.handleResize())

    this.domElement.removeEventListener('contextmenu', e => this.onContextMenu(e))
    this.domElement.removeEventListener('mousedown', e => this.onMouseDown(e))
    this.domElement.removeEventListener('mouseup', e => this.onMouseUp(e))
    this.domElement.removeEventListener('mousemove', e => this.onMouseMove(e))
    this.domElement.removeEventListener('touchstart', e => this.onTouchStart(e))
    this.domElement.removeEventListener('touchend', e => this.onTouchEnd(e))
    this.domElement.removeEventListener('touchmove', e => this.onTouchMove(e))
    this.domElement.removeEventListener('wheel', e => this.onWheel(e))
  }

  /**
     * 获取角色与指定点之间的距离
     */
  public getDistance(target: Vector3): number {
    if (!this.character)
      return 0
    return this.character.position.distanceTo(target)
  }

  /**
     * 获取角色到目标点的方位角（水平角度）
     */
  public getAzimuthalAngle(): number {
    if (!this.character)
      return 0

    const dx = this.character.position.x - this.camera.position.x
    const dz = this.character.position.z - this.camera.position.z

    // 计算方位角（水平角度）
    return Math.atan2(dx, dz)
  }

  /**
     * 获取角色到目标点的极角（垂直角度）
     */
  public getPolarAngle(): number {
    if (!this.character)
      return 0

    const dx = this.character.position.x - this.camera.position.x
    const dy = this.character.position.y - this.camera.position.y
    const dz = this.character.position.z - this.camera.position.z

    // 计算水平距离
    const horizontalDistance = Math.sqrt(dx * dx + dz * dz)

    // 计算极角（垂直角度）
    return Math.atan2(dy, horizontalDistance)
  }
}
